import type { HelpArticle } from "../../types";
import { STRUCTURAL_HELP_FIXTURES } from "../../../calculations/structuralHelpFixtures";

const beam = STRUCTURAL_HELP_FIXTURES.steel_beam_aisc;
const column = STRUCTURAL_HELP_FIXTURES.steel_column_aisc;
const joist = STRUCTURAL_HELP_FIXTURES.cfs_joist;
const stud = STRUCTURAL_HELP_FIXTURES.cfs_stud_wall;
const header = STRUCTURAL_HELP_FIXTURES.cfs_header;
const check = (fixture: typeof beam, key: string) => fixture.checks.find((entry) => entry.limitState === key)!;
const pct = (value: number | null): string => `${((value ?? 0) * 100).toFixed(1)}%`;
const n3 = (value: number | null): string => (value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

export const steelCfsSheetsArticle: HelpArticle = {
  id: "steel-cfs-sheets",
  title: "Steel & cold-formed steel sheets",
  description:
    "Create and read the five manual steel calculators: rolled-I beams and columns, plus exact-row CFS joists, studs, and headers.",
  tags: ["steel", "cold-formed steel", "AISC", "SFIA", "beam", "column", "joist", "stud", "header"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "The Steel family in New calculation contains five manual sheets. Steel Beam and Steel Column calculate capacities from an exact rolled-I shape and grade. The three cold-formed sheets compare entered service loads with exact published SFIA rows. They share the schema workspace, but they do not share an analysis model: choose the sheet whose inputs match the demand you already have.",
    },

    { kind: "heading", level: 2, text: "Choose the calculator", id: "choose" },
    { kind: "where", path: ["Calculations", "Project", "New calculation", "Structural calculators", "Steel"] },
    {
      kind: "paragraph",
      text: "Search by calculator name or summary, then select one card. Firmark creates a sheet with its registry defaults and an independent mark: `SB`, `SC`, `CJ`, `CST`, or `CH`. Steel sheets inherit the project's ASD or LRFD method at creation. CFS sheets are published-row ASD checks and expose no method selector. Every card opens the same two-pane workspace: registry-shaped inputs on the left and the persisted engine record on the right.",
    },
    {
      kind: "shot",
      shotId: "structural-catalog-steel",
      caption: "The Steel catalog family: two calculated rolled-I sheets and three exact-row cold-formed sheets.",
    },
    {
      kind: "table",
      caption: "The five manual sheets and the demand each accepts.",
      headers: ["Sheet", "Manual demand", "What the result checks"],
      rows: [
        ["Steel Beam", "Required M and V; optional simple-span service w", "Major-axis flexure, web shear, optional deflection"],
        ["Steel Column", "Required P, Mx, My; KxLx and KyLy", "Flexural buckling and H1 interaction"],
        ["CFS Joist", "Simple-span D and L in plf", "Matching SFIA total- and live-load rows"],
        ["CFS Stud Wall", "Axial kip/stud plus row-matched lateral pressure", "One published combined axial/lateral capacity"],
        ["CFS Header", "Simple-span D and L in plf", "Published two-member uniform-load capacity"],
      ],
    },

    { kind: "heading", level: 2, text: "Rolled-I beam and column workflow", id: "rolled-i" },
    {
      kind: "paragraph",
      text: "Steel Beam starts with a shape and grade lookup. Browse is scoped to W, S, M, and HP rows; the selected stable keys remain visible under Calculation database. Enter the unbraced length `Lb`, moment-gradient factor `Cb`, and required strengths. `Lb = 0` is reserved for a continuously braced compression flange, as the field's registry help states. Optional serviceability is a paired input: span and uniform service load must both be positive before the deflection check exists.",
    },
    { kind: "registry-group", calculatorId: "steel_beam_aisc", groupId: "materials" },
    {
      kind: "shot",
      shotId: "steel-beam-inputs",
      caption: `${beam.mark}: the W18X50 / A992 keys, ASD method, Lb 0, Mu 184 kip-ft, and Vu 30 kip are visible together.`,
    },
    {
      kind: "values",
      caption: `${beam.mark} · ASD engine record.`,
      rows: [
        { label: "Major-axis flexure", value: `${n3(check(beam, "major_axis_flexure").demand)} / ${n3(check(beam, "major_axis_flexure").capacity)} kip-ft · ${pct(check(beam, "major_axis_flexure").dcr)}` },
        { label: "Web shear", value: `${n3(check(beam, "web_shear").demand)} / ${n3(check(beam, "web_shear").capacity)} kip · ${pct(check(beam, "web_shear").dcr)}` },
        { label: "Service deflection", value: `${n3(check(beam, "service_deflection").demand)} / ${n3(check(beam, "service_deflection").capacity)} in · ${pct(check(beam, "service_deflection").dcr)}` },
      ],
    },
    {
      kind: "paragraph",
      text: "Steel Column replaces beam stability with `Kx`, `Lx`, `Ky`, and `Ly`, then accepts axial compression and biaxial moments. Any nonzero moment requires both an otherwise-supported compact shape and the fully-braced confirmation. For a supported compact shape without that confirmation, the runtime returns the interaction calculation, adds a failed applicability check, and warns that the result is not acceptable for use. A noncompact shape is refused before an interaction record is returned. The worked `SC-1` fixture carries 560 kip and zero moments. Its x-axis slenderness controls: `KL/r = 58.632`, `Fcr = 38.887 ksi`, and ASD compression capacity is 617.074 kip.",
    },
    {
      kind: "shot",
      shotId: "steel-column-results",
      caption: `${column.mark}: axial compression and interaction both carry the engine's ${pct(check(column, "axial_compression").dcr)} ratio.`,
    },
    {
      kind: "callout",
      variant: "note",
      title: "Ratio and current label are separate facts",
      body: `The engine returns passes=true at ${pct(check(column, "axial_compression").dcr)}. The current header rounds that ratio and displays “Near limit · 91%.” This article preserves both values; it does not redefine the product's verdict bands.`,
    },

    { kind: "heading", level: 2, text: "Published-row CFS workflow", id: "cfs" },
    {
      kind: "paragraph",
      text: "CFS selection is a table-row match, not a free-form section analysis. Browse seats a `check_ready` record, and the stable row key remains on the sheet. The entered height, spacing, span, pressure, and stiffener confirmations must agree with that row. Manual mode exposes the simple-span joist and header inputs shown below. It does not expose the engine's separate two-equal-continuous input path. For Joist and Header, the single live-load input is published as floor live `L`; those two sheets cannot publish a roof application as `Lr` or `S` for downstream linking in v1.",
    },
    { kind: "registry-group", calculatorId: "cfs_joist", groupId: "selection" },
    {
      kind: "shot",
      shotId: "cfs-joist-selection",
      caption: `${joist.mark}: paired total and live rows for the same 600S162-33, Fy 33 ksi, 6 ft entry.`,
    },
    {
      kind: "paragraph",
      text: `The joist uses two matching rows because total load and live-load deflection are separate published limits. ${joist.mark} carries D 20 and L 40 plf: total demand 60/168 plf (${pct(check(joist, "total_uniform_load").dcr)}) and live demand 40/168 plf (${pct(check(joist, "live_uniform_load_L_over_360").dcr)}). Its authoritative simple-span packet publishes D 60 lb and L 120 lb at each end for downstream linking.`,
    },
    {
      kind: "paragraph",
      text: `The stud wall uses one combined row. ${stud.mark} matches 362S137-33 at 8 ft, 12 in o.c., and 5 psf, then compares 1.0 kip/stud with 1.46 kip/stud (${pct(check(stud, "combined_axial_and_lateral").dcr)}). The row carries an L/720 marker; the sheet reports that published marker and does not fabricate a deflection value or curve.`,
    },
    {
      kind: "shot",
      shotId: "cfs-stud-results",
      caption: `${stud.mark}: the visible combined axial/lateral check shows Pass, a 5 psf service-lateral basis, and the SFIA table reference.`,
    },
    {
      kind: "paragraph",
      text: `The header is an exact two-member published configuration. ${header.mark} carries D 200 and L 500 plf, so the sheet checks 700 plf against 931 plf (${pct(check(header, "uniform_load").dcr)}). The selected row requires web stiffeners; the fixture confirms them. With that confirmation true, the requirement check passes and each end publishes D 300 lb plus L 750 lb.`,
    },
    {
      kind: "shot",
      shotId: "cfs-header-results",
      caption: `${header.mark}: published uniform-load capacity and the separately reported web-stiffener requirement.`,
    },

    { kind: "heading", level: 2, text: "Read the record before reusing it", id: "record" },
    {
      kind: "paragraph",
      text: "Quick mode keeps the governing banner and check cards visible. Detailed mode adds the full demand/capacity table, engine intermediates, source list, assumptions, and scope limitations. The sheet's standard comes from the engine record when present, with the registry standard as fallback. Lock makes the inputs read-only for review; Export uses the same persisted result. A changed input commits on blur or Enter, while selects and confirmations commit immediately.",
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Each sheet is deliberately bounded",
      body: "The steel record excludes conditions such as noncompact/slender or non-I sections and companion checks listed in Scope & limitations. CFS rows refuse unmatched conditions and do not interpolate. Treat the printed limitations as part of the result, not as optional notes.",
    },

    { kind: "related", topics: ["concrete-foundation-sheets", "masonry-sheets", "section-properties"] },
  ],
};
